/**
 * Feature flags rolled out per tenant from the provisioner (PLAT-FF-1). One
 * rule per flag says who has it:
 *
 *   OFF         nobody
 *   ALL         every tenant
 *   ONLY        the tenants listed
 *   ALL_EXCEPT  every tenant but the ones listed
 *
 * Every app evaluates a rule the same way, from here: operations against
 * its table, ai-operations and the frontend against what operations
 * answers. Runtime values live here (`.cjs`); the types in `.d.cts`.
 */

const FEATURE_FLAG_MODES = ["OFF", "ALL", "ONLY", "ALL_EXCEPT"];

/** A flag's key: dotted lowercase words, e.g. `purchasing.pick-lists`. */
const FEATURE_FLAG_KEY_PATTERN = /^[a-z][a-z0-9]*(?:[-_.][a-z0-9]+)*$/;

/** Whether the rule turns the flag on for this tenant. */
function isFeatureFlagEnabled(rule, tenantId) {
  if (!rule || !tenantId) return false;
  const listed = Array.isArray(rule.tenantIds) && rule.tenantIds.includes(tenantId);
  switch (rule.mode) {
    case "ALL":
      return true;
    case "ONLY":
      return listed;
    case "ALL_EXCEPT":
      return !listed;
    default:
      return false;
  }
}

/** Every rule evaluated for one tenant: `{ [key]: boolean }`. */
function evaluateFeatureFlags(rules, tenantId) {
  const out = {};
  for (const rule of rules) out[rule.key] = isFeatureFlagEnabled(rule, tenantId);
  return out;
}

/**
 * The rule after turning the flag on or off for one tenant, keeping the
 * rest as it was: a tenant joins an ONLY list or leaves an ALL_EXCEPT list
 * to be turned on; leaves an ONLY list or joins an ALL_EXCEPT list to be
 * turned off. OFF becomes ONLY [tenant]; ALL becomes ALL_EXCEPT [tenant].
 */
function applyFeatureFlagTenantSwitch(rule, tenantId, enabled) {
  const ids = Array.isArray(rule.tenantIds) ? rule.tenantIds : [];
  const without = ids.filter((id) => id !== tenantId);
  const withIt = ids.includes(tenantId) ? ids : [...ids, tenantId];
  if (enabled) {
    switch (rule.mode) {
      case "ALL":
        return { mode: "ALL", tenantIds: [] };
      case "ONLY":
        return { mode: "ONLY", tenantIds: withIt };
      case "ALL_EXCEPT":
        return without.length
          ? { mode: "ALL_EXCEPT", tenantIds: without }
          : { mode: "ALL", tenantIds: [] };
      default:
        return { mode: "ONLY", tenantIds: [tenantId] };
    }
  }
  switch (rule.mode) {
    case "ALL":
      return { mode: "ALL_EXCEPT", tenantIds: [tenantId] };
    case "ALL_EXCEPT":
      return { mode: "ALL_EXCEPT", tenantIds: withIt };
    case "ONLY":
      return without.length ? { mode: "ONLY", tenantIds: without } : { mode: "OFF", tenantIds: [] };
    default:
      return { mode: "OFF", tenantIds: [] };
  }
}

/** How a rule reads to a person: "On for 3 tenants", "On for all but 2". */
function describeFeatureFlagRule(rule) {
  const count = Array.isArray(rule.tenantIds) ? rule.tenantIds.length : 0;
  const tenants = `${count} ${count === 1 ? "tenant" : "tenants"}`;
  switch (rule.mode) {
    case "ALL":
      return "On for every tenant";
    case "ONLY":
      return count ? `On for ${tenants}` : "On for nobody yet";
    case "ALL_EXCEPT":
      return count ? `On for all but ${tenants}` : "On for every tenant";
    default:
      return "Off";
  }
}

/**
 * Flags the code depends on. A migration seeds them in every environment,
 * the provisioner can turn them on or off but not remove them, and the code
 * names them through these constants rather than by string.
 */
const PURCHASING_FLAGS = Object.freeze({
  /** Purchase orders, supplier bills, approvals, technicians' field purchases. */
  purchaseOrders: "purchasing.purchase-orders",
  /** Warehouses, trucks, stock, pick lists, counts, the job's materials tab. */
  inventory: "purchasing.inventory",
});

const BUILT_IN_FEATURE_FLAGS = Object.freeze([
  {
    key: PURCHASING_FLAGS.purchaseOrders,
    name: "Purchasing",
    description:
      "Purchase orders, supplier bills, approvals and technicians' field purchases. Suppliers themselves stay available to expenses and the price book.",
  },
  {
    key: PURCHASING_FLAGS.inventory,
    name: "Inventory",
    description:
      "Warehouses, trucks, stock levels, pick lists, counts and the job's materials tab.",
  },
]);

/** Whether the code depends on this flag: it can be turned off, not removed. */
function isBuiltInFeatureFlag(key) {
  return BUILT_IN_FEATURE_FLAGS.some((flag) => flag.key === key);
}

module.exports = {
  FEATURE_FLAG_MODES,
  FEATURE_FLAG_KEY_PATTERN,
  PURCHASING_FLAGS,
  BUILT_IN_FEATURE_FLAGS,
  isBuiltInFeatureFlag,
  isFeatureFlagEnabled,
  evaluateFeatureFlags,
  applyFeatureFlagTenantSwitch,
  describeFeatureFlagRule,
};
